import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency, num, dateShort } from "@/lib/format";
import { SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ShoppingCart, Trash2, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["Wholesale", "D2C", "Retail", "Sample"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [skus, setSkus] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [customer, setCustomer] = useState("");
  const [channel, setChannel] = useState("Wholesale");
  const [facility, setFacility] = useState("");
  const [lines, setLines] = useState([{ sku_code: "", qty: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const items = lines.filter((l) => l.sku_code && Number(l.qty) > 0).map((l) => ({ sku_code: l.sku_code, qty: Number(l.qty) }));
    if (!open || !facility || items.length === 0) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.post("/orders/preview", { customer: customer || "x", channel, facility, items }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facility, JSON.stringify(lines)]);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/skus").then((r) => setSkus(r.data.filter((s) => ["finished", "merchandise"].includes(s.category))));
    api.get("/facilities").then((r) => setFacilities(r.data));
  }, []);

  const skuMap = Object.fromEntries(skus.map((s) => [s.sku_code, s]));
  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { sku_code: "", qty: "" }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const orderValue = lines.reduce((a, l) => a + (Number(l.qty) || 0) * (skuMap[l.sku_code]?.unit_cost || 0), 0);

  const reset = () => { setCustomer(""); setChannel("Wholesale"); setFacility(""); setLines([{ sku_code: "", qty: "" }]); setPreview(null); };

  const submit = async () => {
    const items = lines.filter((l) => l.sku_code && Number(l.qty) > 0).map((l) => ({ sku_code: l.sku_code, qty: Number(l.qty) }));
    if (!customer || !facility || items.length === 0) { toast.error("Add a customer, facility and at least one line item"); return; }
    setSubmitting(true);
    try {
      await api.post("/orders", { customer, channel, facility, items });
      toast.success("Order recorded — finished-goods stock depleted");
      setOpen(false); reset(); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Sales Orders" subtitle="Outbound orders that deplete finished-goods & merch inventory"
        action={<Button data-testid="btn-new-order" onClick={() => { reset(); setOpen(true); }} className="bg-stone-900 hover:bg-stone-800 active:scale-[0.98]"><Plus className="h-4 w-4 mr-1.5" />New Order</Button>} />

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">Order #</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Channel</th>
              <th className="px-4 py-3 text-left">Ships From</th>
              <th className="px-4 py-3 text-right">Items</th>
              <th className="px-4 py-3 text-right">Total Units</th>
              <th className="px-4 py-3 text-right">Order Value</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_no} data-testid={`order-row-${o.order_no}`} onClick={() => setActive(o)}
                className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 cursor-pointer transition-colors">
                <td className="px-4 py-3"><SkuTag code={o.order_no} /></td>
                <td className="px-4 py-3 text-stone-600">{dateShort(o.date)}</td>
                <td className="px-4 py-3 font-medium text-stone-800">{o.customer}</td>
                <td className="px-4 py-3 text-stone-600">{o.channel}</td>
                <td className="px-4 py-3 text-stone-600">{o.facility_name}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{o.items_count}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-700">{num(o.total_qty)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-stone-900">{currency(o.total_value)}</td>
                <td className="px-4 py-3 text-stone-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-stone-400">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-rose-600" />Record Sales Order</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-stone-500">Customer</Label><Input data-testid="order-customer-input" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name" className="mt-1" /></div>
              <div>
                <Label className="text-xs text-stone-500">Channel</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="order-channel-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Ships From</Label>
              <Select value={facility} onValueChange={setFacility}>
                <SelectTrigger data-testid="order-facility-select" className="mt-1"><SelectValue placeholder="Facility" /></SelectTrigger>
                <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-stone-500">Line Items</Label>
              {lines.map((l, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`order-line-${i}`}>
                  <Select value={l.sku_code} onValueChange={(v) => setLine(i, { sku_code: v })}>
                    <SelectTrigger data-testid={`order-line-sku-${i}`} className="flex-1"><SelectValue placeholder="Finished product" /></SelectTrigger>
                    <SelectContent className="max-h-64">{skus.map((s) => <SelectItem key={s.sku_code} value={s.sku_code}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`order-line-qty-${i}`} type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} placeholder="Qty" className="w-24" />
                  {lines.length > 1 && <button data-testid={`order-line-remove-${i}`} onClick={() => removeLine(i)} className="text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
              <button data-testid="order-add-line" onClick={addLine} className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Add line</button>
            </div>
            {preview?.has_warning && (
              <div data-testid="order-fulfillment-warning" className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />Fulfillment alert</div>
                {preview.warnings.map((w) => (
                  <div key={w.sku_code} className="text-xs text-amber-900 flex items-center justify-between gap-2">
                    <span>{w.name}</span>
                    <span className="font-mono">{num(w.current)} → <span className={w.status === "negative" ? "text-rose-700 font-bold" : "font-semibold"}>{num(w.after)}</span> {w.unit} {w.status === "negative" ? "(oversold!)" : `(below reorder ${num(w.reorder_point)})`}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between border-t border-stone-100 pt-3 text-sm">
              <span className="text-stone-500">Order value (at cost)</span>
              <span className="font-mono font-bold text-stone-900">{currency(orderValue)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="order-submit-btn" onClick={submit} disabled={submitting} className="bg-stone-900 hover:bg-stone-800">{submitting ? "Recording…" : "Record Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white max-w-lg">
          {active && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-rose-600" />{active.order_no} · {active.customer}</DialogTitle></DialogHeader>
              <div className="text-sm text-stone-500 border-b border-stone-100 pb-2">{active.channel} · {dateShort(active.date)} · ships from {active.facility_name}</div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 border-b border-stone-200"><th className="px-2 py-2 text-left">Product</th><th className="px-2 py-2 text-right">Qty</th></tr></thead>
                <tbody>
                  {active.items.map((it) => (
                    <tr key={it.sku_code} className="border-b border-stone-50">
                      <td className="px-2 py-2"><div className="font-medium text-stone-800">{skuMap[it.sku_code]?.name || it.sku_code}</div><SkuTag code={it.sku_code} /></td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-rose-600">-{num(it.qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
